import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading, EmptyState } from "../components/UI";
import { Download, Upload, CreditCard, Building2, X, CheckCircle2, FileText } from "lucide-react";

export default function TenantPayments() {
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [tenantId, setTenantId] = useState(null);

  const reload = async () => {
    const [p, s, me] = await Promise.all([
      api.get("/payments"),
      api.get("/settings").catch(()=>({data:null})),
      api.get("/tenants/me").catch(()=>({data:null})),
    ]);
    setPayments(p.data);
    setSettings(s.data);
    setTenantId(me.data?.id || null);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const downloadHistory = async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/payment-history.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payment-history-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Payment history downloaded");
    } catch (err) { toast.error(formatApiError(err) || "Download failed"); }
  };

  const uploadReceipt = async (paymentId, file) => {
    setUploading({...uploading, [paymentId]: true});
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/payments/${paymentId}/upload-receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded. Awaiting manager approval.");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading({...uploading, [paymentId]: false}); }
  };

  if (loading) return <Loading/>;

  const isRentPayment = (p) => p.payment_type === "rent" || p.payment_type === "renewal";

  return (
    <div className="space-y-6">
      <div className="fade-up flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">My Payments</h1>
          <p className="text-[#5E7166] text-sm">Pay via bank transfer or POS and upload your receipt for verification.</p>
        </div>
        {tenantId && (
          <button onClick={downloadHistory} className="btn-outline-emerald text-sm inline-flex items-center gap-1.5 self-start sm:self-auto" data-testid="tenant-download-history-btn">
            <FileText size={14}/> Download Payment History PDF
          </button>
        )}
      </div>

      {settings && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card testid="rent-bank-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E8F5EE] text-[#0E5C3A] flex items-center justify-center"><Building2 size={18}/></div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">RENT PAYMENTS ONLY</div>
                <div className="font-serif font-bold text-lg">{settings.rent_bank_name || settings.bank_name}</div>
                <div className="text-sm">{settings.rent_bank_account_name || settings.bank_account_name}</div>
                <div className="font-mono font-bold text-[#0E5C3A] text-lg mt-1">{settings.rent_bank_account_number || settings.bank_account_number}</div>
              </div>
            </div>
          </Card>
          <Card testid="fees-bank-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FDF6E3] text-[#C9A227] flex items-center justify-center"><Building2 size={18}/></div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">CAUTION, AGREEMENT & SERVICE FEES</div>
                <div className="font-serif font-bold text-lg">{settings.fees_bank_name || "—"}</div>
                <div className="text-sm">{settings.fees_bank_account_name || "—"}</div>
                <div className="font-mono font-bold text-[#0E5C3A] text-lg mt-1">{settings.fees_bank_account_number || "—"}</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card title="Payment History" className="!p-0 overflow-hidden">
        {payments.length === 0 ? <EmptyState icon={CreditCard} title="No payments yet" description="When invoices are issued they will appear here."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Reference</th><th>Type</th><th>Amount</th><th>Status</th><th>Date</th><th>Bank to use</th><th></th></tr></thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} data-testid={`tenant-payment-${p.id}`}>
                    <td className="font-mono text-xs">{p.receipt_number || p.paystack_reference}</td>
                    <td>{statusLabel(p.payment_type)}</td>
                    <td className="font-serif font-bold text-[#0E5C3A]">{naira(p.amount)}</td>
                    <td><span className={statusBadgeClass(p.payment_status)}>{statusLabel(p.payment_status)}</span></td>
                    <td className="text-xs">{formatDate(p.payment_date || p.created_at)}</td>
                    <td className="text-xs">{isRentPayment(p) ? "Rent account" : "Fees account"}</td>
                    <td className="flex gap-2 items-center">
                      {p.payment_status === "pending" && (
                        <label className="btn-gold text-xs cursor-pointer">
                          <Upload size={12}/> {uploading[p.id] ? "Uploading…" : "Upload Receipt"}
                          <input type="file" accept="image/*,application/pdf" onChange={e => e.target.files?.[0] && uploadReceipt(p.id, e.target.files[0])} className="hidden" data-testid={`upload-receipt-${p.id}`}/>
                        </label>
                      )}
                      {p.payment_status === "pending_approval" && <span className="badge badge-gold flex items-center gap-1"><CheckCircle2 size={11}/> Awaiting Approval</span>}
                      {p.payment_status === "paid" && <a href={`${API_BASE}/payments/${p.id}/receipt`} target="_blank" rel="noreferrer" className="btn-outline-emerald text-xs"><Download size={12}/> Receipt</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>
    </div>
  );
}
